/** @jsxRuntime classic */
/** @jsx jsx */
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { displayBlock, parseWidthHeight, positionRelative } from "../../styles/general";
const Collapse = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  children,
  className,
  id,
  onClosed,
  onOpened,
  open,
  style
}, reference) => {
  const ref = useRef(null);
  const timer = useRef(null);
  const _onOpen = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    const scrollHeight = ref.current.scrollHeight;
    const duration = Math.min(scrollHeight, 1500);
    ref.current.style.transitionDuration = duration + 'ms';
    ref.current.style.height = scrollHeight + 'px';
    timer.current = setTimeout(() => {
      if (ref.current) {
        ref.current.style.height = 'auto';
      }
      onOpened && onOpened(ref.current);
    }, duration);
  };
  const _onClose = () => {
    if (ref.current) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      const scrollHeight = ref.current.scrollHeight;
      ref.current.style.height = scrollHeight + 'px';
      timer.current = setTimeout(() => {
        if (ref.current) {
          ref.current.style.height = null;
        }
        onClosed && onClosed(ref.current);
      });
    }
  };
  useEffect(() => {
    if (open) {
      _onOpen();
    } else {
      _onClose();
    }
  }, [open]);
  useImperativeHandle(reference, () => ({
    element: ref.current,
    instance: {}
  }));
  return useMemo(() => jsx("div", {
    css: CollapseRoot,
    ref: ref,
    id: id,
    style: style,
    className: ['DGN-UI-Collapse', className].join(' ').trim().replace(/\s+/g, ' ')
  }, children), [children, className, id, onClosed, onOpened, open, style]);
}));
const CollapseRoot = css`
    ${displayBlock};
    ${positionRelative};
    ${parseWidthHeight('100%', 0)};
    transition: height 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    overflow-y: clip;
`;
Collapse.defaultProps = {
  className: '',
  open: false,
  style: {}
};
Collapse.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** Callback fired when the component closed. */
  onClosed: PropTypes.func,
  /** Callback fired when the component opened. */
  onOpened: PropTypes.func,
  /** If `true`, the content is shown. */
  open: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object
};
export default Collapse;